import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Loader2, FileText, ExternalLink, RefreshCw, Fingerprint, Phone, Globe } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Branding
const LOGO_URL = "/logo.jpg";
const PRACTICE_NAME = "Protea Health Ltd";
const PRACTICE_WEBSITE = "https://adhdclinicjersey.proteahealth.co.uk";
const PRACTICE_PHONE = "01534 484191";

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState("verifying"); // verifying, success, failed, pending
  const [paymentData, setPaymentData] = useState(null);
  const [fingerInkUrl, setFingerInkUrl] = useState(null);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const patientId = searchParams.get("patient_id");
  const checkoutRef = searchParams.get("checkout_ref");
  const isPasskeyLogin = searchParams.get("passkey") === "true";

  useEffect(() => {
    const verifyPayment = async () => {
      // Handle passkey login with existing payment
      if (isPasskeyLogin) {
        const passkeyPaid = sessionStorage.getItem("passkeyLoginPaid");
        const storedFingerInkUrl = sessionStorage.getItem("fingerInkUrl");
        
        if (passkeyPaid === "true" && storedFingerInkUrl) {
          setStatus("success");
          setFingerInkUrl(storedFingerInkUrl);
          setPaymentData({ status: "PAID" });
          sessionStorage.removeItem("passkeyLoginPaid");
          sessionStorage.removeItem("fingerInkUrl");
          return;
        }
      }
      
      // Get checkout ID from URL params first, then session storage
      let checkoutId = checkoutRef || sessionStorage.getItem("checkoutId");
      
      if (!checkoutId) {
        setStatus("failed");
        setError("No payment information found. Please start again.");
        return;
      }
      
      try {
        const response = await axios.get(`${API}/payments/verify/${checkoutId}`);
        const data = response.data;
        
        setPaymentData(data);
        
        if (data.status === "PAID" || data.status === "PENDING_VERIFICATION" || data.form_released) {
          setStatus("success");
          setFingerInkUrl(data.finger_ink_url);
          sessionStorage.removeItem("checkoutId");
          sessionStorage.removeItem("checkoutRef");
          toast.success("Payment verified! Redirecting to form...");
          
          // Auto-redirect to Finger-Ink form after 2 seconds
          if (data.finger_ink_url) {
            setTimeout(() => {
              window.location.href = data.finger_ink_url;
            }, 2000);
          }
        } else if (data.status === "FAILED" || data.status === "CANCELLED") {
          setStatus("failed");
          setError("Payment was not completed. Please try again.");
        } else {
          // Still pending - retry after delay
          setStatus("pending");
          if (retryCount < 10) {
            setTimeout(() => setRetryCount(prev => prev + 1), 3000);
          }
        }
      } catch (err) {
        console.error("Verification error:", err);
        setStatus("failed");
        setError(err.response?.data?.detail || "Failed to verify payment. Please contact support.");
      }
    };

    verifyPayment();
  }, [patientId, checkoutRef, retryCount, isPasskeyLogin]);

  const handleAccessForm = () => {
    if (fingerInkUrl) {
      // Use direct link navigation instead of window.open to avoid popup blockers
      window.location.href = fingerInkUrl;
    }
  };

  const handleRetry = () => {
    navigate("/payment");
  };

  const handleStartOver = () => {
    sessionStorage.clear();
    navigate("/start");
  };

  return (
    <div className={`min-h-screen py-12 px-4 ${status === "success" ? "success-gradient" : "bg-slate-50/50"}`}>
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex justify-center mb-8">
          <span className="text-lg font-semibold text-slate-700">{PRACTICE_NAME}</span>
        </div>
        {/* Verifying State */}
        {status === "verifying" && (
          <Card className="card-elevated animate-fade-in" data-testid="verifying-card">
            <CardContent className="py-16 text-center">
              <Loader2 className="h-16 w-16 text-sky-500 animate-spin mx-auto mb-6" />
              <h2 className="heading-primary text-2xl mb-2">Verifying Payment</h2>
              <p className="text-body">Please wait while we confirm your payment...</p>
            </CardContent>
          </Card>
        )}

        {/* Pending State */}
        {status === "pending" && (
          <Card className="card-elevated animate-fade-in" data-testid="pending-card">
            <CardContent className="py-16 text-center">
              <div className="relative mx-auto w-16 h-16 mb-6">
                <Loader2 className="h-16 w-16 text-amber-500 animate-spin" />
              </div>
              <h2 className="heading-primary text-2xl mb-2">Payment Processing</h2>
              <p className="text-body mb-4">
                Your payment is being processed. This may take a moment...
              </p>
              <p className="text-sm text-slate-500">
                Attempt {retryCount + 1} of 10
              </p>
            </CardContent>
          </Card>
        )}

        {/* Success State */}
        {status === "success" && (
          <div className="space-y-6 animate-fade-in">
            <Card className="card-elevated" data-testid="success-card">
              <CardContent className="py-12 text-center">
                {/* Animated checkmark */}
                <div className="relative mx-auto w-24 h-24 mb-6">
                  <div className="absolute inset-0 bg-emerald-100 rounded-full animate-pulse-soft" />
                  <div className="absolute inset-2 bg-emerald-500 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="h-12 w-12 text-white" />
                  </div>
                </div>
                
                <h2 className="heading-primary text-3xl mb-2">
                  {isPasskeyLogin ? "Welcome Back!" : "Payment Successful!"}
                </h2>
                <p className="text-body text-lg mb-6">
                  {isPasskeyLogin 
                    ? "Your previous payment is still valid" 
                    : "Your payment has been confirmed"
                  }
                </p>
                
                {/* Passkey indicator */}
                {isPasskeyLogin && (
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-4">
                    <Fingerprint className="h-4 w-4" />
                    <span>Signed in with Passkey</span>
                  </div>
                )}
                
                {/* Payment details */}
                {!isPasskeyLogin && (
                  <div className="bg-slate-50 rounded-xl p-4 mb-8 text-left">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-slate-500">Amount Paid</p>
                        <p className="font-semibold text-slate-900">£5.00</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Reference</p>
                        <p className="font-semibold text-slate-900 text-xs">{checkoutRef || paymentData?.checkout_id?.slice(0, 12)}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Important CTA */}
                <div className="bg-sky-50 border border-sky-200 rounded-xl p-5 mb-6">
                  <FileText className="h-8 w-8 text-sky-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-900 mb-2">
                    You can now access your prescription request form
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Click below to open the secure Finger-Ink form
                  </p>
                  
                  <Button 
                    onClick={handleAccessForm}
                    className="w-full btn-primary"
                    data-testid="btn-access-form"
                  >
                    <ExternalLink className="h-5 w-5 mr-2" />
                    Continue to Prescription Request Form
                  </Button>
                </div>
                
                <p className="text-xs text-slate-500">
                  {isPasskeyLogin 
                    ? "You can access the form anytime using your passkey"
                    : "A confirmation has been sent to your email address"
                  }
                </p>
              </CardContent>
            </Card>
            
            {/* Footer */}
            <div className="text-center mt-6">
              <p className="text-sm text-slate-600 font-medium">{PRACTICE_NAME}</p>
              <div className="flex justify-center gap-4 mt-2 text-xs text-slate-500">
                <a href={`tel:${PRACTICE_PHONE}`} className="hover:text-sky-600 flex items-center gap-1">
                  <Phone className="h-3 w-3" />{PRACTICE_PHONE}
                </a>
                <a href={PRACTICE_WEBSITE} target="_blank" rel="noopener noreferrer" className="hover:text-sky-600 flex items-center gap-1">
                  <Globe className="h-3 w-3" />Website
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Failed State */}
        {status === "failed" && (
          <Card className="card-elevated animate-fade-in" data-testid="failed-card">
            <CardContent className="py-12 text-center">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                <AlertCircle className="h-10 w-10 text-red-500" />
              </div>
              
              <h2 className="heading-primary text-2xl mb-2">Payment Not Completed</h2>
              <p className="text-body mb-6">{error}</p>
              
              <div className="space-y-3">
                <Button 
                  onClick={handleRetry}
                  className="w-full btn-primary"
                  data-testid="btn-retry-payment"
                >
                  <RefreshCw className="h-5 w-5 mr-2" />
                  Try Payment Again
                </Button>
                
                <Button 
                  onClick={handleStartOver}
                  variant="outline"
                  className="w-full btn-secondary"
                  data-testid="btn-start-over"
                >
                  Start Over
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
